/**
 * Formulaire de déploiement (§3.2).
 *
 * Le bouton « Déployer » reste désactivé tant que le formulaire est incomplet,
 * et la liste de ce qui manque est affichée juste au-dessus : on n'oblige
 * jamais l'utilisateur à deviner pourquoi il est bloqué.
 */

import {
  appDescriptionSchema,
  MAX_APP_DESCRIPTION_LENGTH,
  MAX_ARTIFACT_CODE_BYTES,
  MAX_COMMENT_LENGTH,
  slugify,
} from '@portail/shared';
import clsx from 'clsx';
import { Check, ClipboardPaste, Lock, RefreshCw, Rocket, Sparkles, X } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, createDeployment, getDomains } from '../api';
import { CodeInput, byteLength } from '../components/CodeInput';
import { ConfirmationCheckbox } from '../components/ConfirmationCheckbox';
import { GuidedForm } from '../components/GuidedForm';
import {
  Button,
  Card,
  ErrorBanner,
  Label,
  PageHeader,
  Select,
  Skeleton,
  TextInput,
} from '../components/ui';
import { messageOf, useAsync } from '../hooks/useAsync';
import { useSession } from '../hooks/useSession';
import { useSlugAvailability, type SlugAvailability } from '../hooks/useSlugAvailability';
import { formatNumber } from '../lib/format';

/** Rendu du verdict de disponibilité du nom. */
function SlugStatus({ availability }: { availability: SlugAvailability }) {
  if (availability.status === 'idle') return null;

  if (availability.status === 'checking') {
    return (
      <p className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
        <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Vérification de la disponibilité…
      </p>
    );
  }

  if (availability.status === 'available') {
    return (
      <p className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
        Ce nom est disponible.
      </p>
    );
  }

  // Mise à jour d'une application existante : c'est un avertissement, pas un
  // refus — d'où l'ambre plutôt que le rouge, et le déploiement reste possible.
  if (availability.status === 'update') {
    return (
      <p
        role="status"
        className="mt-1.5 inline-flex items-start gap-1.5 text-sm text-amber-700 dark:text-amber-400"
      >
        <RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {availability.reason}
      </p>
    );
  }

  return (
    <p
      role="status"
      className="mt-1.5 inline-flex items-start gap-1.5 text-sm text-red-700 dark:text-red-400"
    >
      <X className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {availability.reason}
    </p>
  );
}

/**
 * Exemples de description, choisis pour montrer l'étendue attendue plutôt que pour
 * être recopiés : ce qu'affiche l'application, pour qui, et ce qu'on y fait.
 */
const EXEMPLES_DESCRIPTION = [
  'Un suivi des demandes d’intervention pour l’atelier. Un tableau avec la date, le ' +
    'demandeur, la machine concernée, la description et un état (à faire, en cours, terminé). ' +
    'On peut ajouter une demande par un formulaire, changer son état d’un clic, et filtrer par ' +
    'état. Les demandes terminées passent en gris. Les données doivent être conservées.',
  'Une calculatrice de devis pour la pose de menuiseries. On saisit une surface en m², un type ' +
    'de matériau (PVC, aluminium, bois) et une quantité ; l’application affiche le prix ' +
    'unitaire, le total hors taxes, la TVA à 20 % et le total. Un bouton copie le récapitulatif ' +
    'dans le presse-papiers.',
];

/** Page d'accueil : coller un artifact ou décrire une application, puis la déployer. */
export function DeployPage() {
  const navigate = useNavigate();
  const { settings } = useSession();

  const nameId = useId();
  const domainId = useId();
  const commentId = useId();
  const descriptionId = useId();

  /**
   * Voie de départ. La description passe devant, et devient le défaut.
   *
   * Le collage était le cas d'usage du cahier des charges, écrit quand décrire une
   * application à un modèle n'était pas encore une option du portail. Ce n'est plus
   * le point d'entrée le plus fréquent : la description est accompagnée d'un guide,
   * ne suppose ni de savoir lire du code ni d'avoir encore son artifact sous la
   * main, et couvre le cas de qui n'a rien du tout. Proposer une voie en premier
   * sans qu'elle soit celle par défaut se lirait comme une hésitation.
   */
  const [mode, setMode] = useState<'paste' | 'describe'>('describe');
  /**
   * Le guide est-il actif ? Il l'est d'emblée en mode description, et se retire dès
   * que l'auteur reprend le texte à la main : deux sources modifiables pour un même
   * texte s'écraseraient l'une l'autre.
   */
  const [guide, setGuide] = useState(true);
  const [description, setDescription] = useState('');
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [comment, setComment] = useState('');
  /**
   * Exiger une connexion pour ouvrir l'application.
   *
   * Décidé ici et pas ailleurs : Cloudron n'ajoute pas son addon
   * d'authentification à une application déjà installée. L'activer après coup
   * suppose de supprimer et redéployer, ce que le pipeline dit explicitement.
   */
  const [forceSso, setForceSso] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);

  const domains = useAsync(() => getDomains(), []);

  // Domaine par défaut : le premier proposé par le serveur.
  const availableDomains = domains.data ?? [];
  const effectiveDomain = domain || availableDomains[0]?.domain || '';

  const slug = useMemo(() => slugify(name), [name]);
  const availability = useSlugAvailability(slug, effectiveDomain);

  const resultingUrl = slug && effectiveDomain ? `https://${slug}.${effectiveDomain}` : null;

  /** Première erreur de la description, ou `null` si elle convient. */
  const descriptionError = useMemo(() => {
    const result = appDescriptionSchema.safeParse(description);
    return result.success ? null : (result.error.issues[0]?.message ?? 'Description invalide.');
  }, [description]);

  // Ce qui manque encore, dans l'ordre du formulaire.
  const missing: string[] = [];
  if (mode === 'paste') {
    if (code.trim().length === 0) missing.push("le code de l'artifact");
    if (byteLength(code) > MAX_ARTIFACT_CODE_BYTES) missing.push('un code sous la limite de 1 Mo');
  } else if (descriptionError !== null) {
    missing.push("la description de l'application");
  }
  if (!slug) missing.push("le nom de l'application");
  if (availability.status === 'invalid' || availability.status === 'unavailable') {
    missing.push('un nom valide et disponible');
  }
  if (availability.status === 'checking') missing.push('la fin de la vérification du nom');
  if (!effectiveDomain) missing.push('le domaine');
  if (comment.length > MAX_COMMENT_LENGTH) missing.push('un commentaire plus court');
  if (!confirmed) missing.push('la confirmation ci-dessus');

  const canSubmit = missing.length === 0 && !submitting;

  const onSubmit = (): void => {
    setDescriptionTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    setFailure(null);

    createDeployment({
      // L'un ou l'autre, jamais les deux : le serveur refuse ce cas, et il n'aurait
      // de toute façon aucun moyen de les départager.
      ...(mode === 'paste' ? { code } : { description: description.trim() }),
      slug,
      domain: effectiveDomain,
      comment: comment.trim() ? comment.trim() : null,
      forceSso,
      confirmed: true,
    })
      .then((response) => {
        navigate(`/deploiements/${response.jobId}`);
      })
      .catch((error: unknown) => {
        setFailure(error instanceof ApiError ? error : new ApiError(0, messageOf(error)));
        setSubmitting(false);
      });
  };

  return (
    <>
      <PageHeader
        title="Déployer une application"
        description="Collez le code d'un artifact Claude — ou décrivez l'application que vous voulez. Le portail se charge du reste : écriture ou adaptation du code à nos standards, construction de l'image, mise en ligne avec un certificat TLS."
      />

      <form
        className="grid gap-6 lg:grid-cols-3"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <div className="lg:col-span-2">
          <Card className="space-y-4 p-5">
            {/*
              Deux points de départ, la description en tête. Elle est guidée, ne
              suppose pas de savoir lire du code, et couvre le cas de qui n'a rien
              sous la main. Le collage reste offert à qui arrive avec un artifact
              déjà mis au point dans une conversation.
            */}
            <div
              className="flex flex-wrap items-center gap-2"
              role="group"
              aria-label="Point de départ"
            >
              <Button
                variant={mode === 'describe' ? 'primary' : 'secondary'}
                aria-pressed={mode === 'describe'}
                onClick={() => setMode('describe')}
              >
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Décrire l'application
              </Button>
              <Button
                variant={mode === 'paste' ? 'primary' : 'secondary'}
                aria-pressed={mode === 'paste'}
                onClick={() => setMode('paste')}
              >
                <ClipboardPaste className="h-4 w-4" aria-hidden="true" />
                Coller un code
              </Button>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {mode === 'paste'
                  ? 'Vous avez déjà un artifact Claude.'
                  : 'Vous n’avez pas de code : Claude l’écrit d’après votre description.'}
              </span>
            </div>

            {mode === 'paste' ? (
              <CodeInput value={code} onChange={setCode} />
            ) : guide ? (
              /*
                Le guide est proposé d'emblée, et non caché derrière un bouton
                « besoin d'aide ». Personne ne clique sur ce bouton — et les
                questions qu'il pose sont précisément celles qu'on ne se pose pas
                spontanément : la persistance des données, la forme des graphiques,
                ce qui s'affiche quand une liste est vide.
              */
              <GuidedForm
                onChange={setDescription}
                onDetach={(texte) => {
                  setDescription(texte);
                  setGuide(false);
                  setDescriptionTouched(true);
                }}
              />
            ) : (
              <div>
                <Label
                  htmlFor={descriptionId}
                  required
                  hint={`${formatNumber(description.length)} / ${formatNumber(MAX_APP_DESCRIPTION_LENGTH)}`}
                >
                  Ce que doit faire l'application
                </Label>
                <textarea
                  id={descriptionId}
                  value={description}
                  rows={14}
                  maxLength={MAX_APP_DESCRIPTION_LENGTH}
                  aria-invalid={descriptionTouched && descriptionError !== null}
                  onChange={(event) => setDescription(event.target.value)}
                  onBlur={() => setDescriptionTouched(true)}
                  placeholder="Décrivez l'application en français : ce qu'elle affiche, pour qui, ce qu'on peut y faire, et les règles de calcul s'il y en a…"
                  className={clsx(
                    'w-full rounded-md border bg-white px-3 py-2 text-sm',
                    'text-slate-900 placeholder:text-slate-400 dark:bg-slate-950 dark:text-slate-100',
                    descriptionTouched && descriptionError !== null
                      ? 'border-red-400 dark:border-red-800'
                      : 'border-slate-300 dark:border-slate-700',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
                  )}
                />
                {/* Retour au guide toujours possible, mais il repart de zéro : il
                    ne saurait pas relire un texte repris à la main. Le dire évite
                    de faire perdre une rédaction sur un clic mal compris. */}
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setGuide(true)}
                    className="text-xs text-indigo-700 underline underline-offset-2 dark:text-indigo-300"
                  >
                    Revenir au guide
                  </button>
                  <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">
                    — il repart de questions vierges et remplacera ce texte.
                  </span>
                </div>

                {descriptionTouched && descriptionError !== null ? (
                  <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {descriptionError}
                  </p>
                ) : (
                  <div className="mt-2">
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Tout ce que vous ne précisez pas sera décidé pour vous — et vous sera annoncé
                      avant la mise en ligne. Deux exemples de ce niveau de détail :
                    </p>
                    <ul className="mt-1 space-y-1">
                      {EXEMPLES_DESCRIPTION.map((exemple) => (
                        <li key={exemple}>
                          {/* Cliquable : recopier un exemple pour l'adapter ensuite
                              est un travail que la machine fait mieux. */}
                          <button
                            type="button"
                            onClick={() => {
                              setDescription(exemple);
                              setDescriptionTouched(true);
                            }}
                            className="block w-full rounded px-2 py-1 text-left text-xs italic text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950"
                          >
                            « {exemple} »
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="space-y-5 p-5">
            <div>
              <Label htmlFor={nameId} required hint="minuscules, chiffres et tirets">
                Nom de l'application
              </Label>
              <TextInput
                id={nameId}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Tableau de bord commercial"
                autoComplete="off"
                spellCheck={false}
                aria-describedby={`${nameId}-url`}
              />
              {slug && slug !== name ? (
                <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                  Identifiant retenu : <span className="font-mono">{slug}</span>
                </p>
              ) : null}
              <SlugStatus availability={availability} />
              <p id={`${nameId}-url`} className="mt-2 text-sm">
                <span className="text-slate-500 dark:text-slate-400">Adresse finale : </span>
                {resultingUrl ? (
                  <span className="break-all font-mono text-indigo-700 dark:text-indigo-300">
                    {resultingUrl}
                  </span>
                ) : (
                  <span className="text-slate-400 dark:text-slate-600">
                    https://&lt;nom&gt;.&lt;domaine&gt;
                  </span>
                )}
              </p>
            </div>

            <div>
              <Label htmlFor={domainId} required>
                Domaine
              </Label>
              {domains.loading ? (
                <Skeleton className="h-9 w-full" />
              ) : domains.error ? (
                <ErrorBanner message={domains.error} onRetry={domains.reload} />
              ) : availableDomains.length === 0 ? (
                <p className="text-sm text-red-700 dark:text-red-400">
                  Aucun domaine n'est autorisé pour le déploiement. Contactez le service
                  informatique.
                </p>
              ) : (
                <Select
                  id={domainId}
                  value={effectiveDomain}
                  onChange={(event) => setDomain(event.target.value)}
                >
                  {availableDomains.map((entry) => (
                    <option key={entry.domain} value={entry.domain}>
                      {entry.domain}
                    </option>
                  ))}
                </Select>
              )}
            </div>

            <div>
              <Label
                htmlFor={commentId}
                hint={`${formatNumber(comment.length)} / ${formatNumber(MAX_COMMENT_LENGTH)}`}
              >
                Commentaire (facultatif)
              </Label>
              <textarea
                id={commentId}
                value={comment}
                rows={3}
                maxLength={MAX_COMMENT_LENGTH}
                onChange={(event) => setComment(event.target.value)}
                placeholder="À quoi sert cette application, pour qui, jusqu'à quand… et si ses données doivent être conservées"
                className={clsx(
                  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm',
                  'text-slate-900 placeholder:text-slate-400',
                  'dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
                )}
              />
              {/*
                Ce champ n'est pas qu'une note de contexte : c'est ici que se
                demande la persistance des données, et la fonctionnalité resterait
                introuvable si rien ne le disait. La contrepartie est annoncée dans
                la même phrase, parce qu'elle change qui pourra ouvrir la page.
              */}
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Visible dans « Mes applications » et par le service informatique. Écrivez-y si les
                données saisies ou importées doivent être conservées d’une visite à l’autre :
                l’application recevra une base de données, et demandera alors une connexion à
                l’entreprise pour être ouverte.
              </p>
            </div>

            {/*
              Qui pourra ouvrir l'application. La question est posée ici, au
              déploiement, parce qu'elle ne se rattrape pas : Cloudron n'ajoute pas
              son addon d'authentification à une application déjà installée.
            */}
            <div className="border-t border-slate-200 pt-4 dark:border-slate-800">
              <label className="flex cursor-pointer items-start gap-2.5">
                <input
                  type="checkbox"
                  checked={forceSso}
                  onChange={(event) => setForceSso(event.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-400 text-indigo-600"
                />
                <span>
                  <span className="flex items-center gap-1.5 text-sm font-medium text-slate-900 dark:text-slate-100">
                    <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                    Réserver l’application aux collaborateurs
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                    Une connexion sera exigée avant d’ouvrir la page. Sans cette option, l’adresse
                    est publique — utile pour un formulaire ou une page à partager hors de
                    l’entreprise.
                  </span>
                  <span className="mt-1 block text-xs text-amber-700 dark:text-amber-400">
                    À choisir maintenant : cette protection ne peut pas être ajoutée ensuite sans
                    supprimer puis redéployer l’application.
                  </span>
                </span>
              </label>
            </div>
          </Card>

          <ConfirmationCheckbox
            checked={confirmed}
            onChange={setConfirmed}
            text={settings.confirmationText}
          />

          {failure ? <ErrorBanner message={failure.message} issues={failure.issues} /> : null}

          <div>
            {missing.length > 0 ? (
              <p className="mb-2 text-sm text-slate-600 dark:text-slate-400">
                Il manque encore : {missing.join(', ')}.
              </p>
            ) : null}
            <Button
              type="submit"
              variant="primary"
              disabled={!canSubmit}
              loading={submitting}
              className="w-full"
            >
              <Rocket className="h-4 w-4" aria-hidden="true" />
              {availability.status === 'update' ? 'Mettre à jour' : 'Déployer'}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
